import Link from 'next/link';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import { FaLinkedinIn, FaYoutube } from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';

interface LinksObject {
  type: string;
  link: string;
}

const companyLinks = [
  { href: '/about', label: 'Our Mission' },
  { href: '/founder', label: 'Founder' },
  { href: '/contact#contact-form', label: 'Contact' },
];

const radiologyCenterLinks = [
  { href: '/what-we-offer', label: 'What We Offer' },
  { href: '/virtual-coverage', label: 'Virtual Coverage' },
  { href: '/onsite-coverage', label: 'Onsite Coverage' },
  { href: '/hybrid-coverage', label: 'Hybrid Coverage' },
  { href: '/teleradiology', label: 'Teleradiology' },
];

const resourceLinks = [
  { href: '/forum', label: 'Forum' },
  { href: '/blog', label: 'Blogs' },
  { href: '/news', label: 'In The News' },
  { href: '/infusion', label: 'Infusion Centers' },
  { href: '/residents', label: 'Residents & Fellows' },
];

const legalLinks = [
  { href: '/PrivacyPolicy', label: 'Privacy Policy' },
  { href: '/CopyRights', label: 'Copyright Policy' },
];

const Footer = () => {
  const [links, setLinks] = useState<Record<string, string>>({
    linkedin: '',
    twitter: '',
    facebook: '',
    youtube: '',
  });

  useEffect(() => {
    fetch('https://api.docmoonlight.com/links')
      .then((res) => res.json())
      .then((data) => {
        const socialLinks = Object.fromEntries(
          data.result.map((item: LinksObject) => [item.type, item.link]),
        );
        setLinks(socialLinks);
      })
      .catch(() => {
        setLinks({
          linkedin: '',
          twitter: '',
          facebook: '',
          youtube: '',
        });
      });
  }, []);

  return (
    <footer className='border-t border-[var(--theme-dark-soft)] bg-[linear-gradient(180deg,var(--theme-dark)_0%,var(--theme-dark-soft)_100%)] text-[var(--theme-paper)]'>
      <div className='mx-auto grid max-w-7xl gap-10 px-4 py-14 text-center sm:px-6 lg:grid-cols-[1.2fr_0.8fr_0.8fr_0.8fr] lg:grid-rows-[auto_auto] lg:px-8 lg:text-left'>
        <div className='mx-auto max-w-md lg:row-span-2 lg:mx-0'>
          <Link
            href='/'
            className='flex items-center justify-center text-white lg:justify-start'
          >
            <Image
              src='/images/footer-logo.png'
              alt='DocMoonlight mark'
              width={88}
              height={88}
              className='h-20 w-20 sm:h-24 sm:w-24'
            />
          </Link>
          <p className='mt-5 text-base leading-7 text-[var(--theme-light)]'>
            Flexible physician coverage for radiology and infusion centers, with
            moonlighting opportunities built for residents and fellows.
          </p>
          <div className='mt-6 flex flex-wrap justify-center gap-3 lg:justify-start'>
            {links.linkedin && (
              <a
                target='_blank'
                href={links.linkedin}
                rel='noreferrer'
                aria-label='LinkedIn'
                className='inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#0a66c2] bg-[#0a66c2] text-base text-white transition hover:-translate-y-0.5 hover:bg-[#084f96]'
              >
                <FaLinkedinIn aria-hidden='true' />
              </a>
            )}
            {links.twitter && (
              <a
                target='_blank'
                href={links.twitter}
                rel='noreferrer'
                aria-label='X'
                className='inline-flex h-10 w-10 items-center justify-center rounded-full border border-white bg-white text-base text-black transition hover:-translate-y-0.5 hover:bg-slate-200'
              >
                <FaXTwitter aria-hidden='true' />
              </a>
            )}
            {links.youtube && (
              <a
                target='_blank'
                href={links.youtube}
                rel='noreferrer'
                aria-label='YouTube'
                className='inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#ff0000] bg-[#ff0000] text-base text-white transition hover:-translate-y-0.5 hover:bg-[#cc0000]'
              >
                <FaYoutube aria-hidden='true' />
              </a>
            )}
          </div>
        </div>

        <div className='lg:col-start-2 lg:row-start-1'>
          <h3 className='text-sm font-black uppercase tracking-[0.22em] text-white'>
            Company
          </h3>
          <ul className='mt-5 space-y-3'>
            {companyLinks.map((link) => (
              <li key={link.label}>
                <Link
                  href={link.href}
                  className='text-base text-[var(--theme-light)] transition hover:text-[var(--theme-accent-soft)]'
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className='lg:col-start-3 lg:row-start-1'>
          <h3 className='whitespace-nowrap text-xs font-black uppercase tracking-[0.08em] text-white'>
            Radiology
          </h3>
          <ul className='mt-5 space-y-3'>
            {radiologyCenterLinks.map((link) => (
              <li key={link.label}>
                <Link
                  href={link.href}
                  className='text-base text-[var(--theme-light)] transition hover:text-[var(--theme-accent-soft)]'
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className='lg:col-start-4 lg:row-start-1'>
          <h3 className='text-sm font-black uppercase tracking-[0.22em] text-white'>
            Resources
          </h3>
          <ul className='mt-5 space-y-3'>
            {resourceLinks.map((link) => (
              <li key={link.label}>
                <Link
                  href={link.href}
                  className='text-base text-[var(--theme-light)] transition hover:text-[var(--theme-accent-soft)]'
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className='lg:col-start-4 lg:row-start-2 lg:self-end lg:justify-self-end'>
          <div className='mt-4 flex items-center justify-center gap-3 lg:justify-end'>
            <a
              target='_blank'
              href='https://play.google.com/store/apps/details?id=com.docmoonlight'
              rel='noreferrer'
              className='transition hover:opacity-95'
            >
              <Image
                src='/images/google_play_btn.svg'
                alt='Download on Google Play'
                width={150}
                height={48}
                className='h-12 w-[150px] rounded-[10px] bg-white'
              />
            </a>
            <a
              target='_blank'
              href='https://apps.apple.com/us/app/docmoonlight/id6448665833'
              rel='noreferrer'
              className='transition hover:opacity-95'
            >
              <Image
                src='/images/app_store_btn.svg'
                alt='Download on the App Store'
                width={150}
                height={48}
                className='h-12 w-[150px] rounded-[10px] bg-white'
              />
            </a>
          </div>
        </div>
      </div>

      <div className='border-t border-[rgba(214,214,214,0.12)]'>
        <div className='mx-auto flex max-w-7xl flex-col gap-3 px-4 py-5 text-center text-sm text-[rgba(214,214,214,0.7)] sm:px-6 md:flex-row md:items-center md:justify-between md:text-left lg:px-8'>
          <p>
            Copyright © {new Date().getFullYear()} DOCMOONLIGHT, LLC. All rights
            reserved.
          </p>
          <div className='flex flex-wrap items-center justify-center gap-x-5 gap-y-2 md:justify-end'>
            {legalLinks.map((link) => (
              <Link
                key={link.label}
                href={link.href}
                className='transition hover:text-[var(--theme-accent-soft)]'
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
